import { Component, effect, inject, OnDestroy } from '@angular/core';
import { AccordionModule } from 'primeng/accordion';
import { AtlasIndicatorTreeComponent } from '../../organisms/atlas-indicator-tree/atlas-indicator-tree.component';
import { Button } from 'primeng/button';
import { IndicatorListComponent } from '../../organisms/indicator-list/indicator-list.component';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { getState } from '@ngrx/signals';
import { WidgetStore } from '../../../stores/grid/widget.state';
import {
  CustomGridStackWidget,
  PoiConf,
  WegweiserIndicator,
} from '../../../services/grid-widget-config.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ContextMenuModule } from 'primeng/contextmenu';
import { NgClass } from '@angular/common';
import { BadgeModule } from 'primeng/badge';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DividerModule } from 'primeng/divider';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { IndicatorSelectionListComponent } from '../indicator-selection-list/indicator-selection-list.component';
import { WegweiserStore } from '../../../stores/wegweiser-api/wegweiser.state';
import { TreeModule } from 'primeng/tree';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { WidgetHelperService } from '../../../services/widget/widget-helper.service';
import { DebugComponent } from '../../atoms/debug/debug.component';
import { Indicator } from '../../../models/indicator';
import { SvgIconComponent } from '../../atoms/svg-icon/svg-icon.component';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { DataLayerType } from '../../../models/data-layer-type';

@Component({
  selector: 'app-widget-indicators',
  standalone: true,
  imports: [
    AccordionModule,
    AtlasIndicatorTreeComponent,
    IndicatorListComponent,
    Button,
    ContextMenuModule,
    NgClass,
    BadgeModule,
    SelectButtonModule,
    DividerModule,
    IndicatorSelectionListComponent,
    TreeModule,
    AlertComponent,
    DebugComponent,
    SvgIconComponent,
  ],
  templateUrl: './widget-indicators.component.html',
  styleUrl: './widget-indicators.component.scss',
})
export class WidgetIndicatorsComponent implements OnDestroy {
  activeWidget: CustomGridStackWidget | null = null;
  readonly indicatorStore = inject(IndicatorStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly layerStore = inject(DataLayerStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);
  readonly widgetStore = inject(WidgetStore);
  readonly wegweiserStore = inject(WegweiserStore);

  mode: 'list' | 'tree' = 'tree';
  selectionMode = 'multiple';
  combineableIndicatorIds: string[] | null = null;

  constructor(
    private messageService: ConfirmationDialogService,
    private widgetHelperService: WidgetHelperService
  ) {
    effect(() => {
      const widgetState = getState(this.widgetStore);
      const wegweiserState = getState(this.wegweiserStore);
      this.activeWidget = this.widgetStore.selected();

      if (
        this.activeWidget &&
        this.widgetHelperService.isContentWidget(this.activeWidget.type)
      ) {
        this.combineableIndicatorIds = this.indicatorStore.allIds();
        return;
      }
    });
  }

  isPoiDataLayerType(): boolean {
    const typeId = this.activeWidget?.widget?.dataLayerType;
    if (!typeId) return false;
    const type = this.layerTypeStore.getById(typeId);
    return type?.type === 'Point';
  }

  isContentWidget() {
    return (
      this.activeWidget &&
      this.widgetHelperService.isContentWidget(this.activeWidget.type)
    );
  }

  /**
   * Updates the filter criteria in the indicator store based on the provided search query.
   *
   * @param {string} $event - The search query string to be used for updating the filter.
   * @return {void} This method does not return a value.
   */
  search($event: string): void {
    this.indicatorStore.updateFilter({
      query: $event,
      tags: undefined,
      products: undefined,
      ids: undefined,
      published: null,
      locked: null,
      category: [],
    });
  }

  /**
   * Toggles the selection state of an indicator by its identifier. If the indicator is currently
   * selected, it will be removed from the selection; if it is not selected, it will be added.
   *
   * @param {string} id The identifier of the indicator to toggle selection for.
   * @return {void} This method does not return a value.
   */
  toggleIndicatorSelection(id: string): void {
    if (this.activeWidget) {
      const indicators = this.activeWidget.widget.indicators;
      if (indicators.includes(id)) {
        indicators.splice(indicators.indexOf(id), 1);
      } else {
        indicators.push(id);
      }
      this.activeWidget.widget.indicator = indicators[0] || '';
      this.setLayerByIndicatorSelection();

      this.activeWidget.widget.indicators = indicators;
      this.updateWidget(this.activeWidget);
    }
  }

  /**
   * Updates the active widget's data layer and year based on indicator selections.
   * It filters and retrieves available data layers according to the widget's indicator
   * and data layer type, and assigns the newest applicable layer if the current one is not valid.
   *
   * @return {void} No return value.
   */
  setLayerByIndicatorSelection(): void {
    if (!this.activeWidget) return;

    //filter tree
    if (this.activeWidget.widget.indicators.length === 0) {
      this.combineableIndicatorIds = null;
      //this.indicatorStore.updateFilter({ ids: undefined });
      return;
    }

    if (!this.activeWidget.widget.dataLayerType) return;
    this.combineableIndicatorIds =
      this.activeWidget.widget.indicators.length > 0
        ? this.indicatorDataStore.getIndicatorIdsByDataLayerTypeAndIndicators(
            this.activeWidget.widget.dataLayerType,
            this.activeWidget.widget.indicators
          )
        : null;

    const layerIds =
      this.indicatorDataStore.getLayerIdsByDataLayerTypeAndIndicators(
        this.activeWidget.widget.dataLayerType,
        this.activeWidget.widget.indicators
      );

    const activeLayerId = this.activeWidget.widget.dataLayer;
    const activeLayer = activeLayerId
      ? this.layerStore.getById(activeLayerId)
      : undefined;
    const activeLayerStillValid =
      !!activeLayer && layerIds.includes(activeLayer._id);

    const sortedLayers = layerIds.length
      ? this.layerStore.getSortedByIds(layerIds)
      : [];
    // Prefer the newest non-forecast layer; only fall back to a forecast layer
    // if none of the candidates are non-forecast.
    const newestLayer =
      sortedLayers.find(layer => !layer.isForecast) ?? sortedLayers[0] ?? null;

    // Keep the current layer if it's still valid and there's no non-forecast
    // alternative to switch to.
    if (
      activeLayerStillValid &&
      (!activeLayer?.isForecast || newestLayer?.isForecast)
    ) {
      return;
    }

    if (!newestLayer) {
      this.messageService.showMessage(
        'Nicht kombinierbar',
        'Ihre aktuelle Indikatorenauswahl hat keine Schnittmenge in den zur Verfügungstehenden Jahren. Sie können die Indikatoren aber im Jahresverlauf anzeigen (Diagramm-Aufbau unter "Anpassen").',
        8000
      );

      return;
    }

    this.activeWidget.widget.dataLayer = newestLayer._id;
    this.activeWidget.widget.year = String(newestLayer.year);
  }

  /**
   *
   */
  onDrop(event: CdkDragDrop<string[]>): void {
    if (!this.activeWidget?.widget.indicators) return;
    moveItemInArray(
      this.activeWidget.widget.indicators,
      event.previousIndex,
      event.currentIndex
    );
    this.updateWidget(this.activeWidget);
  }

  /**
   * Updates the specified widget in the widget store.
   *
   * @param {CustomGridStackWidget} widget - The widget object containing updated information.
   * @return {void} This method does not return a value.
   */
  updateWidget(widget: CustomGridStackWidget): void {
    this.widgetStore.update(widget.id, widget);
    if (widget.widget.context === 'local') {
      this.widgetStore.updateDistricts(widget.id, widget.widget.dataLayer);
      this.widgetStore.updateChartData();
    } else {
      this.widgetStore.updateChartData();
      this.widgetStore.rerenderWidget(widget.id);
    }
  }

  /**
   * Sets the selection mode of the component.
   *
   * @param {'list' | 'tree'} mode - The desired selection mode, either 'list' or 'tree'.
   * @param {Event} event - The event object associated with the action.
   * @return {void} Does not return a value.
   */
  setSelectionMode(mode: 'list' | 'tree', event: Event): void {
    event.stopPropagation(); // Prevent the click event from triggering the accordion's toggle.
    this.mode = mode;
  }

  /**
   * Updates the indicator selection for the active widget based on the provided event data.
   *
   * @param $event An array of strings representing the new indicators to be applied to the active widget.
   * @return void
   */
  updateIndicatorSelection($event: string[]): void {
    if (!this.activeWidget?.widget) return;
    const widget = this.activeWidget.widget;
    if (widget.context === 'external' && widget.wegweiserData) {
      //widget.wegweiserData.indicators = $event;
      const wegweiserIndicators = [...widget.wegweiserData.indicators];
      const updatedIndicators: WegweiserIndicator[] = $event
        .map(id => wegweiserIndicators.find(i => i.id === Number(id)))
        .filter(indicator => indicator !== undefined) as WegweiserIndicator[];

      widget.wegweiserData.indicators = [...updatedIndicators];
      widget.wegweiserRequestParams.indicatorIds = $event.map(id => Number(id));
    } else if (widget.context === 'local') {
      if (!this.activeWidget?.widget.indicators) return;
      widget.indicators = $event;
      widget.indicator = widget.indicators[0];
    }

    this.activeWidget.widget = { ...widget };
    this.updateWidget(this.activeWidget);
  }

  /**
   * Toggles the selection of a specific indicator in the active widget.
   * Adds the indicator ID if it is not in the indicators list, or removes it if it is already selected.
   * Updates the widget's indicators, parameters, and widget data accordingly.
   *
   * @param {string} id - The ID of the indicator to be toggled in the widget's selection.
   * @return {void} This method does not return any value.
   */
  toggleWegweiserIndicatorSelection(id: string): void {
    if (this.activeWidget) {
      const indicators = this.activeWidget.widget.indicators;

      if (indicators.includes(id)) {
        indicators.splice(indicators.indexOf(id), 1);
      } else {
        indicators.push(id);
      }

      const wegweiserYears = this.wegweiserStore.getCommonYearsByIndicators(
        indicators.map(id => Number(id))
      );

      this.activeWidget.widget.indicator = indicators[0] || '';
      this.activeWidget.widget.indicators = indicators;
      this.activeWidget.widget.wegweiserRequestParams.years =
        this.activeWidget.widget.focusProperty === 'dataLayer'
          ? [wegweiserYears[wegweiserYears.length - 1]]
          : wegweiserYears;

      this.activeWidget.widget.wegweiserRequestParams.indicatorIds =
        indicators.map(id => Number(id));

      //if (this.activeWidget.widget.wegweiserData) {
      //  const indSlugs = this.activeWidget.widget.wegweiserData.indicators.map(
      //    (ind: WegweiserIndicator) => ind.friendlyUrl
      //  );
      //  const regionSlugs = this.activeWidget.widget.wegweiserData.regions.map(
      //    (reg: any) => reg.friendlyUrl
      //  );
      //  const yearsParam =
      //    wegweiserYears.length > 1
      //      ? wegweiserYears[0] +
      //        '-' +
      //        wegweiserYears[wegweiserYears.length - 1]
      //      : String(wegweiserYears[0]);
      //  const url = `/${indSlugs.join('+')}+${regionSlugs.join('+')}+${yearsParam}`;
      //}
      this.wegweiserStore.updateWegweiserDataAndWriteToWidget(
        this.activeWidget
      );
    }
  }

  getPoiIndicators(poiId: string): undefined | Indicator[] {
    const year = this.activeWidget?.widget?.year;
    if (!year) return undefined;
    const dataLayer = this.layerStore.filterByParentAndYear(
      poiId,
      Number(year)
    );
    if (!dataLayer) return undefined;
    return this.indicatorDataStore
      .filterByDataLayer(dataLayer._id)
      .map(item => {
        return this.indicatorStore.getById(item.indicator);
      })
      .filter(item => !!item);
  }

  togglePoiIndicator(id: string, conf: PoiConf) {
    if (!this.activeWidget) return;
    const indicators = conf.indicators || [];
    if (indicators.includes(id)) {
      indicators.splice(indicators.indexOf(id), 1);
    } else {
      if (this.activeWidget.widget.dataLayerType === conf.type) {
        if (this.selectionMode === 'single') {
          indicators.length = 0;
        }
        if (this.selectionMode === 'double' && indicators.length >= 2) {
          indicators.shift();
        }
        if (this.selectionMode === 'triple' && indicators.length >= 3) {
          indicators.shift();
        }
      }
      indicators.push(id);
    }
    conf.indicators = indicators;

    if (this.activeWidget.widget.dataLayerType === conf.type) {
      this.activeWidget.widget.indicator = indicators[0] || '';
      this.activeWidget.widget.indicators = indicators;
    } else {
      const widgetPoiConf = this.activeWidget.widget.map?.poi?.find(
        poi => poi.type === conf.type
      );
      if (widgetPoiConf) {
        widgetPoiConf.indicators = conf.indicators;
      }
    }

    this.updateWidget(this.activeWidget);
  }

  /**
   * check if poi has layers for given year
   *
   * @param {number|string} year - The year for which the POI layer is to be retrieved. Can be provided as a number or string.
   * @param {string} poi - The type of POI for which the layer is to be retrieved.
   * @return {boolean} returns true when the poi has layers in given year
   */
  poiAvailableInYear(year: number | string, poi: string): boolean {
    return this.layerStore.parentHasYear(poi, Number(year));
  }

  /**
   * Retrieves a list of Points of Interest (POIs) available for the active widget's year, based on the provided configuration.
   *
   * @param {Record<string, PoiConf | null>} poiConfig - Configuration of POIs, where keys represent POI IDs and values contain their corresponding configuration or null if not configured.
   * @return {Array<any>} A list of POIs available for the active widget's year.
   */
  getPoiWithData(poiConfig: PoiConf[]) {
    const year = this.activeWidget?.widget?.year;
    if (!this.activeWidget || !year) return [];

    const result = poiConfig.filter(conf =>
      this.poiAvailableInYear(year, conf.type)
    );

    if (this.isPoiDataLayerType()) {
      const typeId = this.activeWidget.widget.dataLayerType;
      if (!result.find(p => p.type === typeId)) {
        if (this.poiAvailableInYear(year, typeId)) {
          result.unshift({
            type: typeId,
            indicators: [...this.activeWidget.widget.indicators],
          });
        }
      }
    }

    return result;
  }

  getTypeOfPoiConfig(typeId: string): DataLayerType | undefined {
    return this.layerTypeStore.getById(typeId);
  }

  ngOnDestroy(): void {
    this.indicatorStore.clearFilter();
  }
}
